import { z } from 'zod';
import { QuietHoursSchema } from './outbox';

/** Keys of the global Setting table and their schemas. Read/write via SettingsService (apps/api/src/core/settings). */
export const SETTING_SCHEMAS = {
  'safety.globalKillSwitch': z.boolean(),
  'safety.quietHours': QuietHoursSchema,
  /** Group id (Group.id) of the internal test group — the only real destination before go-live (D-65) */
  'safety.testGroupId': z.string().nullable(),
  /** staging only: allowed waChatIds; everything else is blocked */
  'safety.stagingAllowlist': z.array(z.string()),
  'notify.emailFrom': z.string(),
  'llm.enabled': z.boolean(),
  'llm.model': z.string(),
  'general.appName': z.string(),
  'general.defaultLanguage': z.enum(['he', 'en']),
  'general.businessHours': z.object({ days: z.array(z.number().int().min(0).max(6)), start: z.string(), end: z.string() }),
  /** D-131: each change a client makes on its portal settings page also emails the first-line notification recipients */
  'portal.notifyStaff': z.boolean(),
  /** D-121 / W2-3-17: the compliance alert car-21 ("הנחיה להצהרה כוזבת / הפרת רגולציה") exists only when true; then still off per client until chosen */
  'features.complianceAlert': z.boolean(),
  /** D-122 retro mode: open a retro run automatically on a burst of late calls / after a silent source (off: an admin opens it) */
  'features.retroAuto': z.boolean(),
  /** D-125 WhatsApp people: the LID -> phone resolve and the address-book sync jobs run only when true (DEPLOY-C, after OpenWA >= 0.25.0) */
  'features.waPeopleJobs': z.boolean(),
  /** D-127 / R17 (c3): the weekly network report v2 blocks (cohort, closers, stars, loss reasons, coaching); off = the 1B weekly text */
  'features.networkWeeklyV2': z.boolean(),
  /** D-128 / R17 (c3): the Speed Lease setters desk's own brief lines (W2-6-20); off = the 1B brief */
  'features.settersBrief': z.boolean(),
  /** D-128 / R17 (c3): the setters line in the network brief (W2-6-21); off = no line */
  'features.networkSettersLine': z.boolean(),
  /** D-132 / W3-4-12: the first weekly network report of a month links the monthly network page (off until Orel approves a preview) */
  'features.networkMonthlyPage': z.boolean(),
  /** D-156: the "איך נגמר" line on call alerts and the watch call lines (off until a test-group sample per segment; endState is stored either way) */
  'features.endStateLine': z.boolean(),
  /** D-156 option B: a call alert whose call ended well drops one level (never legal / publicity / watch / a variant); off = option A */
  'features.endStateSeverity': z.boolean(),
  /** D-154 / W4B-14: the daily brief v2 (daily_v2) and the weekly leaders / helps by the agent score; off = the v1 texts, byte-identical */
  'features.briefV2': z.boolean(),
  /** wave 5 (trust in alerts): a CRITICAL alert of a call whose NIVISION report arrived after staleHours (up to 48 h) is still sent, with an "arrived late" line; off = skipped as stale */
  'features.lateCritical': z.boolean(),
  /** D-123 call-PII retention: null a call's phone / name / summary texts N months after the call (off until the production dry run) */
  'retention.callPii.enabled': z.boolean(),
  'retention.callPii.months': z.number().int().min(6).max(60),
  /** 26.10.2026: per site (Site.id), votes of its calls for the PBX clock of NIVISION's call.startedAt (true UTC or the Israel wall clock labelled "Z") */
  'nivision.siteClocks': z.record(z.string(), z.object({ wall: z.number().min(0), utc: z.number().min(0), at: z.string() })),
  /** D-147: addresses (IP or CIDR) NIVISION webhooks may come from; an event from any other address is quarantined (stored, not processed, incident). [] = no check */
  'nivision.ingressAllowIps': z.array(z.string().trim().min(3).max(64)).max(50),
  /** W4A-7 (b): (site, local date) pairs left out of the brief comparisons (daily comparison days, the weekly's previous week) — a day whose labels changed */
  'summaries.compareExcludedDays': z
    .array(z.object({ siteId: z.string(), date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), reason: z.string().max(200) }))
    .max(500),
  /**
   * D-158 (part D): the daily brief's comparable days read DailyAggregate.metrics (one site, car, a stored day of the current
   * version) instead of re-counting its 14-day lookback; off = the lookback (byte-identical either way, aggregate-comparable-days.db.spec)
   */
  'summaries.comparableFromAggregate': z.boolean(),
  /** D-152: every agent baseline window (watch bad day / streak / good day, car-04 tone) starts no earlier than this Israel local date; null = no limit */
  'alerts.agentBaselineSince': z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
} as const;

export type SettingKey = keyof typeof SETTING_SCHEMAS;
export type SettingValue<K extends SettingKey> = z.infer<(typeof SETTING_SCHEMAS)[K]>;

export const SETTING_DEFAULTS: { [K in SettingKey]: SettingValue<K> } = {
  'safety.globalKillSwitch': false,
  'safety.quietHours': QuietHoursSchema.parse({}),
  'safety.testGroupId': null,
  'safety.stagingAllowlist': [],
  'notify.emailFrom': 'NIVISION WhatsApp <wa-alerts@nivision.ai>',
  'llm.enabled': true,
  'llm.model': 'gemini-3.8-flash',
  'general.appName': 'NIVISION WhatsApp',
  'general.defaultLanguage': 'he',
  'general.businessHours': { days: [0, 1, 2, 3, 4], start: '09:00', end: '18:00' },
  'portal.notifyStaff': false,
  'features.complianceAlert': false,
  'features.retroAuto': false,
  'features.waPeopleJobs': false,
  'features.networkWeeklyV2': false,
  'features.settersBrief': false,
  'features.networkSettersLine': false,
  'features.networkMonthlyPage': false,
  'features.endStateLine': false,
  'features.endStateSeverity': false,
  'features.briefV2': false,
  'features.lateCritical': false,
  'retention.callPii.enabled': false,
  'retention.callPii.months': 13,
  'nivision.siteClocks': {},
  // NIVISION's production egress (Orel 26.10; every /in/nivision request of the day came from it)
  'nivision.ingressAllowIps': ['3.127.167.149'],
  'summaries.compareExcludedDays': [],
  'summaries.comparableFromAggregate': false,
  'alerts.agentBaselineSince': null,
};
